import { supabase } from './supabase';
import { STAFF_ASSISTANT_NAME } from './assistant';

// Dr Cleanstein's engine (the ask-chat edge function), the same one the Portal's
// Ask panel uses. It streams one JSON event per line. From a chat, the open
// WhatsApp chat's id goes with the question so Dr C knows who it is about; the
// function reads that chat with the operator's own login, so nothing new is opened.

export type DrcEvent =
  | { type: 'conversation'; id: string }
  | { type: 'step'; label: string }
  | { type: 'text'; text: string }
  | { type: 'done'; status: string }
  | { type: 'error'; message: string };

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ask-chat`;

function parseEvent(line: string): DrcEvent | null {
  const trimmed = line.trim();
  if (!trimmed) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(trimmed);
  } catch {
    return null;
  }
  if (!raw || typeof raw !== 'object') return null;
  const e = raw as Record<string, unknown>;
  switch (e.type) {
    case 'conversation': return typeof e.id === 'string' ? { type: 'conversation', id: e.id } : null;
    case 'step': return typeof e.label === 'string' ? { type: 'step', label: e.label } : null;
    case 'text': return typeof e.text === 'string' ? { type: 'text', text: e.text } : null;
    case 'error': return typeof e.message === 'string' ? { type: 'error', message: e.message } : null;
    case 'done': return { type: 'done', status: typeof e.status === 'string' ? e.status : 'done' };
    default: return null;
  }
}

/** Ask a question; events arrive as the answer is written. Resolves when the turn ends. */
export async function askDrc(
  params: { text: string; drcConversationId: string | null; chatId: string | null; origin?: 'suggest'; signal?: AbortSignal },
  onEvent: (event: DrcEvent) => void,
): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    onEvent({ type: 'error', message: 'You are signed out. Please sign in again.' });
    return;
  }
  let response: Response;
  try {
    response = await fetch(FUNCTION_URL, {
      method: 'POST',
      headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
      signal: params.signal,
      body: JSON.stringify({
        action: 'ask',
        role: 'ted',
        text: params.text,
        conversation_id: params.drcConversationId,
        chat_conversation_id: params.chatId,
        ...(params.origin ? { origin: params.origin } : {}),
      }),
    });
  } catch {
    if (params.signal?.aborted) { onEvent({ type: 'done', status: 'stopped' }); return; }
    onEvent({ type: 'error', message: 'No connection. Check your signal and try again.' });
    return;
  }

  if (!response.ok || !response.body) {
    let message = `${STAFF_ASSISTANT_NAME} is not available right now.`;
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // keep the plain message
    }
    onEvent({ type: 'error', message });
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const emit = (line: string) => {
    const event = parseEvent(line);
    if (event) onEvent(event);
  };
  for (;;) {
    let chunk: ReadableStreamReadResult<Uint8Array>;
    try {
      chunk = await reader.read();
    } catch {
      // Stopped by the person (Claude's Stop button, Gary 18 Oct) or the line dropped.
      onEvent(params.signal?.aborted ? { type: 'done', status: 'stopped' } : { type: 'error', message: 'The answer was cut off. Try again.' });
      return;
    }
    const { value, done } = chunk;
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let newline = buffer.indexOf('\n');
    while (newline >= 0) {
      emit(buffer.slice(0, newline));
      buffer = buffer.slice(newline + 1);
      newline = buffer.indexOf('\n');
    }
  }
  emit(buffer + decoder.decode());
}

/** Plain text of an answer (for "Use in reply"): see lib/answer.ts. */
export { plainAnswer } from './answer';

// ---------------------------------------------------------------- Saved Dr C chats
// The same store as Sentinel's Ask page (ask_conversations), the person's own only (row
// rules). origin 'ask' is the Ask history; 'customer_chat' is help asked inside a WhatsApp
// chat, reopened there; 'suggest' drafts are never listed (Gary, 15 Oct).

export interface DrcChatSummary {
  id: string; title: string | null; updated_at: string;
  /** Starred and filed in a project, as in Claude (Gary, 18 Oct). */
  starred_at?: string | null;
  project_id?: string | null;
  /** The last question asked, for the list's second line (Gary, 18 Oct). */
  lastQuestion?: string;
  questions?: number;
}
export interface DrcTurn { question: string; answer: string }

export async function listDrcChats(userId: string): Promise<DrcChatSummary[]> {
  const { data, error } = await supabase.from('ask_conversations').select('id, title, updated_at, starred_at, project_id')
    .eq('user_id', userId).eq('role', 'ted').eq('origin', 'ask').order('updated_at', { ascending: false }).limit(200);
  if (error) throw new Error(error.message);
  const chats = (data ?? []) as DrcChatSummary[];
  if (!chats.length) return chats;
  // The questions, newest first, for each chat's last question and how many were asked.
  const { data: asked } = await supabase.from('ask_messages').select('conversation_id, content')
    .in('conversation_id', chats.slice(0, 100).map((c) => c.id)).eq('role', 'user').order('created_at', { ascending: false }).limit(1000);
  const last = new Map<string, string>();
  const count = new Map<string, number>();
  for (const row of asked ?? []) {
    const blocks = Array.isArray(row.content) ? (row.content as { type?: string; text?: string }[]) : [];
    const text = blocks.filter((b) => b.type === 'text' && typeof b.text === 'string').map((b) => b.text as string).join('').replace(ADDED_NOTE, '').trim();
    if (!text) continue;
    count.set(row.conversation_id, (count.get(row.conversation_id) ?? 0) + 1);
    if (!last.has(row.conversation_id)) last.set(row.conversation_id, text);
  }
  return chats.map((c) => ({ ...c, lastQuestion: last.get(c.id), questions: count.get(c.id) }));
}

/** The latest help asked inside this WhatsApp chat, so it reopens where it was left. */
export async function latestChatHelp(userId: string, chatId: string): Promise<string | null> {
  const { data } = await supabase.from('ask_conversations').select('id')
    .eq('user_id', userId).eq('role', 'ted').eq('origin', 'customer_chat').eq('chat_conversation_id', chatId)
    .order('updated_at', { ascending: false }).limit(1).maybeSingle();
  return (data?.id as string | undefined) ?? null;
}

// Context lines the engine adds to a stored question, never shown back.
// The note can hold its own brackets ("WhatsApp profile name (chosen by the customer...)"), so it runs to the end.
export const ADDED_NOTE = /\s*\((Asked from the portal page|Asked from CMB Chats about)[\s\S]*\)\.?\s*$/;

/** Questions and written answers only; one answer can span several stored messages. */
export async function loadDrcChat(conversationId: string): Promise<DrcTurn[]> {
  const { data, error } = await supabase.from('ask_messages').select('role, content')
    .eq('conversation_id', conversationId).order('created_at');
  if (error) throw new Error(error.message);
  const turns: DrcTurn[] = [];
  for (const row of data ?? []) {
    const blocks = Array.isArray(row.content) ? (row.content as { type?: string; text?: string }[]) : [];
    const text = blocks.filter((b) => b.type === 'text' && typeof b.text === 'string').map((b) => b.text as string).join('');
    if (!text) continue;
    if (row.role === 'user') turns.push({ question: text.replace(ADDED_NOTE, ''), answer: '' });
    else if (turns.length) turns[turns.length - 1]!.answer += text;
  }
  return turns;
}

// ---------------------------------------------------------------- Projects and stars (Gary, 18 Oct)
// Claude's folders: each person's own (ask_projects, row rules), shared with Sentinel's Ask page.

export interface DrcProject { id: string; name: string; description: string | null }

export async function listDrcProjects(): Promise<DrcProject[]> {
  const { data, error } = await supabase.from('ask_projects').select('id, name, description').order('name');
  if (error) throw new Error(error.message);
  return (data ?? []) as DrcProject[];
}

export async function createDrcProject(name: string): Promise<DrcProject> {
  const { data, error } = await supabase.from('ask_projects').insert({ name: name.trim().slice(0, 80) }).select('id, name, description').single();
  if (error) throw new Error(error.message);
  return data as DrcProject;
}

export async function renameDrcProject(id: string, name: string): Promise<void> {
  const { error } = await supabase.from('ask_projects').update({ name: name.trim().slice(0, 80) }).eq('id', id);
  if (error) throw new Error(error.message);
}

/** Its chats go back to Recents (the database moves them). */
export async function deleteDrcProject(id: string): Promise<void> {
  const { error } = await supabase.from('ask_projects').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

export async function starDrcChat(id: string, starred: boolean): Promise<void> {
  const { error } = await supabase.from('ask_conversations').update({ starred_at: starred ? new Date().toISOString() : null }).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function moveDrcChat(id: string, projectId: string | null): Promise<void> {
  const { error } = await supabase.from('ask_conversations').update({ project_id: projectId }).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function renameDrcChat(id: string, title: string): Promise<void> {
  const { error } = await supabase.from('ask_conversations').update({ title: title.slice(0, 200) }).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteDrcChat(id: string): Promise<void> {
  const { error } = await supabase.from('ask_conversations').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

/** Today, Yesterday, Previous 7 days, Previous 30 days, Older, as Sentinel groups them. */
export function drcChatGroup(updatedAt: string, now = new Date()): string {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const t = new Date(updatedAt).getTime();
  const day = 86_400_000;
  if (t >= start) return 'Today';
  if (t >= start - day) return 'Yesterday';
  if (t >= start - 7 * day) return 'Previous 7 days';
  if (t >= start - 30 * day) return 'Previous 30 days';
  return 'Older';
}

